/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/*
 * iON (decision 83, 08·03; QA 2.11): Element Call shows "Calling…" only once
 * the ring notification is sent, after the call is joined — on a slow
 * network that is half a minute of a call already going on, with its timer
 * and buttons. Until then, an outgoing call of two that waits for pickup is
 * "Calling…" too, with the one called taken from the room members.
 * The timer of the call starts when the other side answers, not when the
 * call window opens.
 */

import { useEffect, useState } from "react";
import { type Room as MatrixRoom } from "matrix-js-sdk";

import { useUrlParams } from "../UrlParams";

export interface IonCallee {
  userId: string;
  name: string;
  avatarUrl?: string;
}

/** The other member of a room of two, invited or joined. */
export function calleeOf(room: MatrixRoom): IonCallee | null {
  const me = room.client.getUserId();
  const others = [
    ...room.getMembersWithMembership("join"),
    ...room.getMembersWithMembership("invite"),
  ].filter((m) => m.userId !== me);
  if (others.length !== 1) return null;
  const [m] = others;
  return {
    userId: m.userId,
    name: m.name,
    avatarUrl: m.getMxcAvatarUrl() ?? undefined,
  };
}

/**
 * @param ringing whether Element Call already shows its ringing
 * @param others how many others are in the call
 * @returns the one called, while the call waits for the ring to be sent
 * (else null), and when the call was answered
 */
export function useIonAwaitingPickup(
  room: MatrixRoom,
  ringing: boolean,
  others: number,
): { callee: IonCallee | null; answeredAt: number } {
  const { waitForCallPickup, sendNotificationType } = useUrlParams();
  const waits = waitForCallPickup && sendNotificationType === "ring";
  // Once it rang or someone came, this phase is over for good
  const [over, setOver] = useState(!waits);
  const [answeredAt, setAnsweredAt] = useState(Date.now);
  const [answered, setAnswered] = useState(!waits);
  useEffect(() => {
    if (!over && (ringing || others > 0)) setOver(true);
  }, [over, ringing, others]);
  useEffect(() => {
    if (!answered && others > 0) {
      setAnswered(true);
      setAnsweredAt(Date.now());
    }
  }, [answered, others]);
  const callee = !over && !ringing && others === 0 ? calleeOf(room) : null;
  return { callee, answeredAt };
}
